import { useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import { hasNumbersOnly } from '../../../../helpers/helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { addLuxer, removeLuxer } from '../../../../reducers/buildingSelectorReducer'

const MAX_LOCKERS = 5

const LockerSelect = ({
  building,
  placeholder,
  label,
  addLuxer = () => {},
  removeLuxer = () => {},
}) => {
  const { t } = useTranslation(['organisation'])
  const [isLoading, setIsLoading] = useState(false)
  const [values, setValues] = useState([])
  const [inputValue, setInputValue] = useState('')
  const { displaySnackbar } = useSnackbar()

  const canAdd = inputValue && hasNumbersOnly(inputValue) && values.length < MAX_LOCKERS

  const handleAddLocker = async () => {
    if (!inputValue || isLoading) return
    setIsLoading(true)
    try {
      await addLuxer(building.id, inputValue)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveLocker = async (values, { removedValue }) => {
    if (!removedValue || isLoading) return
    setIsLoading(true)
    try {
      await removeLuxer(building.id, removedValue.value)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setIsLoading(false)
    }
  }

  const onAddOption = useCallback(() => {
    if (!canAdd) return
    const optionIndex = values.findIndex((option) => option.label === inputValue)
    if (optionIndex === -1) {
      handleAddLocker()
    }
    setInputValue('')
  }, [inputValue, values])

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onAddOption()
    }
  }

  const onInputChange = (value, { action }) => {
    if (action === 'input-change') setInputValue(value)
  }

  useEffect(() => {
    if (building && building.luxerOneLocations) {
      const options = building.luxerOneLocations.map((location) => ({
        value: location.id,
        label: location.luxerOneId,
      }))
      setValues(options)
    }
  }, [building])

  return (
    <div className="ctp-locker">
      {label && <label className="label">{label}</label>}
      <div>
        <Select
          className="locker-select-container"
          classNamePrefix="select-primary"
          placeholder={placeholder}
          disabled={!building || isLoading}
          isClearable={false}
          isMulti
          options={values}
          value={values}
          inputValue={inputValue}
          onInputChange={onInputChange}
          onChange={handleRemoveLocker}
          onKeyDown={onKeyDown}
          components={{
            DropdownIndicator: null,
            NoOptionsMessage: () => null,
            Menu: () => null,
          }}
        />
        <button
          className={`button button-primary auto-width ${isLoading ? 'is-loading' : ''}`}
          type="button"
          disabled={!canAdd || !building}
          onClick={onAddOption}
        >
          {t('organisation:shared.actions.add')}
        </button>
      </div>
    </div>
  )
}

LockerSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  building: PropTypes.object,
  addLuxer: PropTypes.func,
  removeLuxer: PropTypes.func,
}

export default connect(null, {
  addLuxer,
  removeLuxer,
})(LockerSelect)
